import { useLayoutEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { AlertTriangle, Bot, Loader2, MessageSquare, Pause, Play, Send, User, UserRound } from 'lucide-react'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchConversationMessages, pauseConversation, resumeConversation, sendAgentMessage } from '../api'
import type { ConversationDetail as ConversationDetailType, MessageCursor } from '../api'
import { formatMessageTimestamp, timeAgo } from '../utils/time'
import { Avatar } from './Avatar'

const SCROLL_LOAD_MORE_THRESHOLD_PX = 80

interface Props {
  conversation: ConversationDetailType | undefined
  isLoading: boolean
  isError: boolean
  hasSelection: boolean
}

function formatCost(usd: number): string {
  if (usd === 0) return '$0.00'
  if (usd < 0.01) return `$${usd.toFixed(6)}`
  return `$${usd.toFixed(4)}`
}

export function ConversationDetail({ conversation, isLoading, isError, hasSelection }: Props) {
  const queryClient = useQueryClient()
  const [agentText, setAgentText] = useState('')
  const phone = conversation?.phone
  const scrollRef = useRef<HTMLDivElement>(null)
  // Antes de pedir mensajes más viejos guardamos el scrollHeight actual para
  // restaurar la posición relativa de lectura después de insertarlos arriba
  // — si no, el navegador salta al tope apenas crece el contenido.
  const prevScrollHeightRef = useRef<number | null>(null)

  const messagesQuery = useInfiniteQuery({
    queryKey: ['conversation-messages', phone],
    queryFn: ({ pageParam }) => fetchConversationMessages(phone!, pageParam),
    initialPageParam: undefined as MessageCursor | undefined,
    getNextPageParam: (lastPage) => (lastPage.has_more ? (lastPage.next_cursor ?? undefined) : undefined),
    enabled: hasSelection && !!phone,
  })

  // Cada página llega en orden cronológico ascendente, pero data.pages está
  // ordenado de la más reciente (fetch inicial) a la más vieja (páginas
  // siguientes) — hay que invertir el ARRAY DE PÁGINAS, no cada página.
  // Guarda defensiva: descarta páginas/items con forma inesperada en vez de
  // crashear el render (ej. una respuesta no-JSON servida por error).
  const messages = messagesQuery.data
    ? [...messagesQuery.data.pages].reverse().flatMap((page) =>
        Array.isArray(page?.items) ? page.items.filter((m) => m && typeof m.role === 'string') : [],
      )
    : []

  useLayoutEffect(() => {
    const container = scrollRef.current
    if (!container) return
    if (prevScrollHeightRef.current !== null) {
      container.scrollTop = container.scrollHeight - prevScrollHeightRef.current
      prevScrollHeightRef.current = null
      return
    }
    if (messagesQuery.data?.pages.length === 1) {
      // Primera página de esta conversación (recién seleccionada): ir al mensaje más reciente.
      container.scrollTop = container.scrollHeight
    }
  }, [messagesQuery.data])

  const handleScroll = () => {
    const container = scrollRef.current
    if (!container || messagesQuery.isFetchingNextPage || !messagesQuery.hasNextPage) return
    if (container.scrollTop < SCROLL_LOAD_MORE_THRESHOLD_PX) {
      prevScrollHeightRef.current = container.scrollHeight
      messagesQuery.fetchNextPage()
    }
  }

  const togglePause = useMutation({
    mutationFn: (paused: boolean) =>
      paused ? resumeConversation(conversation!.phone) : pauseConversation(conversation!.phone),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', conversation?.phone] })
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    },
  })

  const sendMessage = useMutation({
    mutationFn: (text: string) => sendAgentMessage(conversation!.phone, text),
    onSuccess: () => {
      setAgentText('')
      queryClient.invalidateQueries({ queryKey: ['conversation', conversation?.phone] })
      queryClient.invalidateQueries({ queryKey: ['conversation-messages', conversation?.phone] })
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    },
  })

  const handleSend = () => {
    const text = agentText.trim()
    if (!text || sendMessage.isPending) return
    sendMessage.mutate(text)
  }

  if (!hasSelection) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white/70 text-center text-slate-500 dark:border-slate-800 dark:bg-slate-900/60">
        <MessageSquare className="h-9 w-9" />
        <p className="text-sm">Selecciona una conversación para ver el historial.</p>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white/70 text-center text-rose-500 dark:border-slate-800 dark:bg-slate-900/60">
        <AlertTriangle className="h-9 w-9" />
        <p className="text-sm">No se pudo cargar esta conversación.</p>
        <p className="text-xs text-slate-400 dark:text-slate-600">
          Puede que el historial haya expirado (TTL de 24h) o el backend no esté disponible.
        </p>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white/70 shadow-sm dark:border-slate-800 dark:bg-slate-900/60 dark:shadow-black/20">
      <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-6 py-4 dark:border-slate-800">
        {isLoading || !conversation ? (
          <div className="h-6 w-48 animate-pulse rounded bg-slate-100 dark:bg-slate-800/60" />
        ) : (
          <>
            <div className="flex items-start gap-3">
              <Avatar name={conversation.client_name} phone={conversation.phone} />
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                    {conversation.client_name || conversation.phone}
                  </h2>
                  <AnimatePresence>
                    {conversation.handoff_requested && (
                      <motion.span
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="rounded-full bg-rose-500/10 px-2 py-0.5 text-xs font-medium text-rose-700 ring-1 ring-rose-500/30 dark:text-rose-300"
                      >
                        Pide agente humano
                      </motion.span>
                    )}
                    {conversation.paused && (
                      <motion.span
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-amber-500/30 dark:text-amber-300"
                      >
                        Pausado
                      </motion.span>
                    )}
                  </AnimatePresence>
                </div>
                <p className="text-xs text-slate-500">
                  {conversation.phone} · {conversation.message_count} mensajes ·{' '}
                  {timeAgo(conversation.last_timestamp)}
                </p>
                {conversation.handoff_requested && (
                  <p className="mt-0.5 text-xs text-rose-500 dark:text-rose-400">
                    {conversation.handoff_reason && `"${conversation.handoff_reason}" · `}
                    solicitado {timeAgo(conversation.handoff_requested_at)}
                  </p>
                )}
                <p className="mt-0.5 text-xs text-slate-400 dark:text-slate-600">
                  ~{(conversation.input_tokens_total + conversation.output_tokens_total).toLocaleString('es-CR')}{' '}
                  tokens · {formatCost(conversation.estimated_cost_usd)} estimado (aproximado)
                </p>
              </div>
            </div>

            <motion.button
              type="button"
              disabled={togglePause.isPending}
              onClick={() => togglePause.mutate(conversation.paused)}
              whileTap={{ scale: 0.95 }}
              className={`flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                conversation.paused
                  ? 'bg-emerald-600/90 text-white hover:bg-emerald-500'
                  : 'bg-amber-600/90 text-white hover:bg-amber-500'
              }`}
            >
              {conversation.paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
              {conversation.paused ? 'Reanudar' : 'Pausar'}
            </motion.button>
          </>
        )}
      </div>

      <div ref={scrollRef} onScroll={handleScroll} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6 py-5">
        {(isLoading || messagesQuery.isLoading) && (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-12 w-2/3 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800/60" />
            ))}
          </div>
        )}

        {messagesQuery.isFetchingNextPage && (
          <div className="flex justify-center py-1">
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          </div>
        )}

        <AnimatePresence initial={false}>
          {!isLoading &&
            !messagesQuery.isLoading &&
            messages
              .filter((m) => m.role !== 'system')
              .map((message) => {
                const isAssistant = message.role === 'assistant'
                const isAgent = message.role === 'agent'
                const isBotSide = isAssistant || isAgent
                return (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, ease: 'easeOut' }}
                    className={`flex items-start gap-2 ${isBotSide ? 'justify-start' : 'justify-end'}`}
                  >
                    {isBotSide && (
                      <div
                        className={`mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                          isAgent
                            ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300'
                            : 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-300'
                        }`}
                      >
                        {isAgent ? <UserRound className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                      </div>
                    )}
                    <div className={`flex max-w-[75%] flex-col gap-1 ${isBotSide ? 'items-start' : 'items-end'}`}>
                      <div
                        className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap shadow-sm ${
                          isAgent
                            ? 'bg-emerald-600 text-white shadow-emerald-950/30'
                            : isAssistant
                              ? 'bg-slate-100 text-slate-900 shadow-slate-300/40 dark:bg-slate-800 dark:text-slate-100 dark:shadow-black/10'
                              : 'bg-indigo-600 text-white shadow-indigo-950/30'
                        }`}
                      >
                        {message.content}
                      </div>
                      <span className="px-1 text-[11px] text-slate-400 dark:text-slate-600">
                        {formatMessageTimestamp(message.created_at)}
                      </span>
                    </div>
                    {!isBotSide && (
                      <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                        <User className="h-4 w-4" />
                      </div>
                    )}
                  </motion.div>
                )
              })}
        </AnimatePresence>
      </div>

      {conversation?.paused && (
        <div className="flex items-center gap-2 border-t border-slate-200 px-6 py-3 dark:border-slate-800">
          <input
            type="text"
            value={agentText}
            onChange={(e) => setAgentText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSend()
            }}
            placeholder="Responder como agente humano..."
            className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          />
          <motion.button
            type="button"
            disabled={!agentText.trim() || sendMessage.isPending}
            onClick={handleSend}
            whileTap={{ scale: 0.95 }}
            className="flex shrink-0 items-center gap-1.5 rounded-lg bg-emerald-600/90 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Send className="h-3.5 w-3.5" />
            Enviar
          </motion.button>
        </div>
      )}
    </div>
  )
}
